import { useRef, useState } from 'react';
import { toEssencePool, hasMinimumEssence, resolveEssenceShortfall, describeItemAffix, listRerollCandidates, type EssencePool } from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';
import { Button, EssenceTally, Popover } from '@sorrem-client/ui/components';
import { toEquipment } from '@sorrem-client/ui/shared/inventory-item';
import { describeShortfall, REROLL_COST_GROWTH_WORDS } from '../../crafting-preview';
import styles from './RerollAffix.module.scss';

interface RerollAffixProps {
    readonly item: UIInventoryItem;
    readonly affixIndex: number;
    readonly banked: Partial<EssencePool>;
    /** What rerolling any one of the item's affixes costs. */
    readonly cost: Partial<EssencePool>;
    readonly isWorking: boolean;
    /** Why the last reroll failed, or null. */
    readonly error: string | null;
    /** Rerolls the affix; resolves to whether it landed. */
    readonly onReroll: () => Promise<boolean>;
    /** The vessel shows this reroll while the link is hovered or its confirmation is open; false goes back. */
    readonly onPreview: (isPreviewed: boolean) => void;
}

/**
 * The reroll link at the end of an affix line on Crafting's left card
 * (#287): it asks before spending, in a popover saying what the affix could
 * become and what it costs. While it is hovered or asking, the vessel panel
 * shows what the reroll leaves.
 */
export function RerollAffix({ item, affixIndex, banked, cost, isWorking, error, onReroll, onPreview }: RerollAffixProps): JSX.Element | null {
    const anchorRef = useRef<HTMLSpanElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const affix = item.instance?.affixes[affixIndex];
    if (affix === undefined) {
        return null;
    }

    const affixText = describeItemAffix(affix);
    const candidates = listRerollCandidates(toEquipment(item), affixIndex);
    const shortfall = describeShortfall(resolveEssenceShortfall(banked, cost));
    const rerollCount = item.instance?.rerollCount ?? 0;

    const close = (): void => {
        setIsOpen(false);
        onPreview(false);
    };
    const confirm = async (): Promise<void> => {
        if (await onReroll()) close();
    };

    return (
        <span
            ref={anchorRef}
            className={styles.rerollAffix}
            onPointerEnter={() => onPreview(true)}
            onPointerLeave={() => { if (!isOpen) onPreview(false); }}
        >
            <Button variant='link' size='small' aria-label={`Reroll ${affixText}`} onClick={() => { setIsOpen(true); onPreview(true); }}>
                Reroll
            </Button>
            <Popover open={isOpen} anchorRef={anchorRef} placement='right' onClose={close}>
                <div className={styles.confirm} role='dialog' aria-label={`Reroll ${affixText}`}>
                    <p className={styles.title}>{`Reroll ${affixText}?`}</p>
                    <p className={styles.text}>
                        {`It becomes ${candidates.length === 1 ? 'the one other affix' : `one of ${candidates.length} others`}: ${candidates.map((candidate) => candidate.label).join(', ')}.`}
                    </p>
                    {rerollCount > 0 && <p className={styles.text}>{`Rerolled ${rerollCount} ${rerollCount === 1 ? 'time' : 'times'}; each reroll costs ${REROLL_COST_GROWTH_WORDS} as much as the last.`}</p>}
                    <EssenceTally essencePool={toEssencePool(cost)} ariaLabel='Essence the reroll costs' label='Costs' isEmptyHidden />
                    {shortfall !== null && <p className={styles.problem}>{shortfall}</p>}
                    {error !== null && <p className={styles.problem} role='alert'>{error}</p>}
                    <div className={styles.actions}>
                        <Button size='small' disabled={isWorking || !hasMinimumEssence(banked, cost)} onClick={() => void confirm()}>Reroll</Button>
                        <Button variant='link' size='small' onClick={close}>Cancel</Button>
                    </div>
                </div>
            </Popover>
        </span>
    );
}
